import React, { useCallback, useEffect, useMemo } from 'react'
import { ActivityIndicator } from 'react-native'

import { env } from '@env'
import { RouteProp, useRoute } from '@react-navigation/native'
import { LinkBroken } from '@zet/common/components/linkBroken'
import { useAgentDetailsWF } from '@zet/customer/src/api/hooks/webFlow'
import { Stack } from '@zet/design-system'
import { initHash } from '@zet/utils/loginHash'

import { useChoiceConnectLeadId } from '../../api/brands/hooks'

type RouteParams = {
  insurance_type: 'bike' | 'car'
  leadId?: string
  lead_uuid?: string
}

declare global {
  interface Window {
    MotorInsuranceWidget?: (containerId: string, config: object) => void
  }
}

const ChoiceConnect: React.FC = () => {
  const hash = initHash()
  const { data } = useAgentDetailsWF()
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>()
  const searchParams = useMemo(() => {
    // Check if we're in browser environment
    if (typeof window === 'undefined') return new URLSearchParams()
    return new URLSearchParams(window.location.search)
  }, [])

  const insuranceTypeParam =
    route.params?.insurance_type ??
    (searchParams.get('insurance_type') as RouteParams['insurance_type'] | null)
  const choiceConnectLeadId =
    route.params?.lead_uuid ??
    (searchParams.get('lead_uuid') as RouteParams['lead_uuid'] | null)
  const leadId =
    route.params?.leadId ??
    (searchParams.get('leadId') as RouteParams['leadId'] | null)

  // Call API to get choiceConnectLeadId if not present in URL but leadId is available
  const {
    data: apiChoiceConnectLeadId,
    isLoading: isApiLoading,
    error: apiError,
  } = useChoiceConnectLeadId(
    !choiceConnectLeadId && leadId ? leadId : undefined,
  )

  // Use the choiceConnectLeadId from URL or API response
  const finalChoiceConnectLeadId = choiceConnectLeadId || apiChoiceConnectLeadId

  // Update URL with lead_uuid when API returns data
  useEffect(() => {
    if (
      apiChoiceConnectLeadId &&
      !choiceConnectLeadId &&
      typeof window !== 'undefined'
    ) {
      const url = new URL(window.location.href)
      url.searchParams.set('lead_uuid', apiChoiceConnectLeadId)
      window.history.replaceState({}, '', url.toString())
    }
  }, [apiChoiceConnectLeadId, choiceConnectLeadId])

  const initializeWidget = useCallback(() => {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return
    }

    const config = {
      theme: {
        mode: 'light',
        options: {
          palette: {
            primary: { main: '#0F2B40' },
            secondary: { main: '#265BFF' },
            background: { default: '#F5F5F5' },
          },
        },
      },
      partner_config: {
        X_API_KEY: '',
        CLIENT_CODE: 'choice_connect',
        SOURCE: 'zet_web',
        UUID: finalChoiceConnectLeadId || '',
        AGENT_CODE: env.CHOICE_CONNECT_AGENT_CODE,
        SUB_AGENT_CODE: leadId || '',
        VEHICLE_TYPE: insuranceTypeParam ? insuranceTypeParam : '',
      },
    }

    if (typeof window.MotorInsuranceWidget !== 'undefined') {
      window.MotorInsuranceWidget('insuranceWidgetContainer', config)
    } else {
      console.warn('MotorInsuranceWidget is not available')
    }
  }, [finalChoiceConnectLeadId, leadId, insuranceTypeParam])

  useEffect(() => {
    // Only run side effects if hash and data are valid
    if (!hash || !data) {
      return
    }

    // Check if we're in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    // Don't initialize if we're waiting for API response and don't have choiceConnectLeadId
    if (!choiceConnectLeadId && leadId && isApiLoading) {
      return
    }

    document.title = 'Connect Motor Insurance'
    const scriptElement = document.createElement('script')
    scriptElement.src = `${env.CHOICE_CONNECT_WIDGET_URL}widget/widget.js`
    scriptElement.async = true
    scriptElement.onload = () => {
      initializeWidget()
    }
    scriptElement.onerror = (error) => {
      console.error('Failed to load widget script:', error)
    }
    document.body.appendChild(scriptElement)
    return () => {
      if (document.body.contains(scriptElement)) {
        document.body.removeChild(scriptElement)
      }
    }
  }, [
    hash,
    data,
    initializeWidget,
    choiceConnectLeadId,
    leadId,
    isApiLoading,
    apiError,
  ])

  // Conditional return after all hooks
  if ((!hash || !data) && isApiLoading) {
    return <LinkBroken />
  }

  // Show loading state if we're waiting for API response
  if (!choiceConnectLeadId && leadId && isApiLoading) {
    return (
      <Stack className="flex-1 bg-white items-center justify-center">
        <ActivityIndicator size="large" />
      </Stack>
    )
  }

  return (
    <Stack className="flex-1 bg-white">
      <div id="insuranceWidgetContainer" style={{ height: '100vh' }} />
    </Stack>
  )
}

export default ChoiceConnect
